/**
 * Admin Activity Log - Global activity tracking for owners
 * Provides oversight of all team activities across all cases
 */

import React, { useState, useEffect, useMemo } from 'react';
import { usePermissions } from '../contexts/PermissionsContext';
import AppLayout from '../components/AppLayout';
import { api } from '../utils/api';
import { Activity, Search, Filter, Download, Calendar, User, FileText,
  Eye, Edit3, Share2, Trash2, MessageSquare, Copy, Zap,
  ChevronDown, ChevronLeft, ChevronRight, RefreshCw, X
} from 'lucide-react';

// Activity type definitions
interface ActivityItem {
  id: string;
  report_id: string;
  user_id: string;
  user_email: string;
  user_name?: string;
  action_type: string;
  action_details: string;
  section?: string;
  ip_address?: string;
  user_agent?: string;
  metadata?: Record<string, any>;
  timestamp: string;
  // Joined data
  client_name?: string;
  business_name?: string;
}

interface ActivityStats {
  total_activities: number;
  activities_today: number;
  unique_users: number;
  most_active_user: string;
  action_breakdown: Record<string, number>;
}

export default function AdminActivityLog() {
  const { role } = usePermissions();
  
  // Data state
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [stats, setStats] = useState<ActivityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateRange, setDateRange] = useState('7'); // days
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 50;
  
  // Unique users for filter dropdown
  const [uniqueUsers, setUniqueUsers] = useState<{email: string; name?: string}[]>([]);

  // Action type configurations
  const actionConfig: Record<string, { icon: React.ReactNode; color: string; label: string; bgColor: string }> = {
    view: { icon: <Eye className="w-4 h-4" />, color: 'var(--color-needs-human-text)', bgColor: 'var(--color-needs-human-tint)', label: 'Viewed' },
    edit: { icon: <Edit3 className="w-4 h-4" />, color: 'var(--color-needs-human-text)', bgColor: 'var(--color-needs-human-tint)', label: 'Edited' },
    share: { icon: <Share2 className="w-4 h-4" />, color: 'var(--color-verified)', bgColor: 'transparent', label: 'Shared' },
    download: { icon: <Download className="w-4 h-4" />, color: 'var(--color-needs-human-text)', bgColor: 'var(--color-needs-human-tint)', label: 'Downloaded' },
    generate: { icon: <Zap className="w-4 h-4" />, color: 'var(--color-needs-human-text)', bgColor: 'var(--color-needs-human-tint)', label: 'Generated' },
    delete: { icon: <Trash2 className="w-4 h-4" />, color: 'var(--color-needs-human-text)', bgColor: 'var(--color-needs-human-tint)', label: 'Deleted' },
    duplicate: { icon: <Copy className="w-4 h-4" />, color: 'var(--color-needs-human-text)', bgColor: 'var(--color-needs-human-tint)', label: 'Duplicated' },
    comment: { icon: <MessageSquare className="w-4 h-4" />, color: 'var(--color-text-muted)', bgColor: 'var(--color-bg-elevated)', label: 'Commented' },
    invite: { icon: <User className="w-4 h-4" />, color: 'var(--color-needs-human-text)', bgColor: 'var(--color-needs-human-tint)', label: 'Invited' },
    add_member: { icon: <User className="w-4 h-4" />, color: 'var(--color-verified)', bgColor: 'transparent', label: 'Added Member' },
    remove_member: { icon: <User className="w-4 h-4" />, color: 'var(--color-needs-human-text)', bgColor: 'var(--color-needs-human-tint)', label: 'Removed Member' },
    update_role: { icon: <User className="w-4 h-4" />, color: 'var(--color-needs-human-text)', bgColor: 'var(--color-needs-human-tint)', label: 'Updated Role' },
    calculate: { icon: <Zap className="w-4 h-4" />, color: 'var(--color-needs-human-text)', bgColor: 'var(--color-needs-human-tint)', label: 'Calculated' },
  };

  // Load activities
  const loadActivities = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('per_page', perPage.toString());
      params.append('days', dateRange);
      if (userFilter !== 'all') params.append('user_email', userFilter);
      if (actionFilter !== 'all') params.append('action_type', actionFilter);
      if (searchQuery) params.append('search', searchQuery);

      const response = await api.get(
        `/api/v1/activity/admin/all?${params.toString()}`
      );

      setActivities(response.activities || []);
      setTotalPages(response.total_pages || 1);
      setStats(response.stats || null);
      setUniqueUsers(response.unique_users || []);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load activities:', err);
      setError(err.message || 'Failed to load activity log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadActivities();
  }, [page, userFilter, actionFilter, dateRange]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) {
        loadActivities();
      } else {
        setPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Format timestamp
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatFullDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Timestamp', 'User', 'Email', 'Action', 'Details', 'Report/Client', 'IP Address'];
    const rows = activities.map(a => [
      new Date(a.timestamp).toISOString(),
      a.user_name || 'Unknown',
      a.user_email,
      a.action_type,
      a.action_details || '',
      a.client_name || a.report_id,
      a.ip_address || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity_log_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Check permissions - only owners can access
  if (role !== 'owner' && role !== 'admin') {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Activity className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-text-ghost)' }} />
            <h2 className="cs-h2">Access Restricted</h2>
            <p className="cs-meta mt-2">Only owners can access the activity log.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 py-6" style={{ background: 'var(--color-bg-base)' }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="cs-display flex items-center gap-3">
              <Activity className="w-7 h-7" style={{ color: 'var(--color-brand)' }} />
              Activity Log
            </h1>
            <p className="cs-meta mt-1">Monitor team activity across all cases</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={loadActivities}
              className="p-2 rounded-lg transition"
              style={{ color: 'var(--color-text-muted)' }}
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={exportToCSV}
              className="cs-body flex items-center gap-2 px-4 py-2 rounded-lg transition"
              style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', fontWeight: 500 }}
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="rounded-xl p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <p className="cs-meta">Total Activities</p>
              <p className="cs-figure-lg">{stats.total_activities.toLocaleString()}</p>
            </div>
            <div className="rounded-xl p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <p className="cs-meta">Today</p>
              <p className="cs-figure-lg" style={{ color: 'var(--color-brand)' }}>{stats.activities_today.toLocaleString()}</p>
            </div>
            <div className="rounded-xl p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <p className="cs-meta">Active Users</p>
              <p className="cs-figure-lg">{stats.unique_users}</p>
            </div>
            <div className="rounded-xl p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <p className="cs-meta">Most Active</p>
              <p className="cs-h3 truncate">{stats.most_active_user || 'N/A'}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="rounded-xl p-4 mb-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
              <input
                type="text"
                placeholder="Search by user, client, report ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="cs-body w-full pl-10 pr-4 py-2 rounded-lg"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
              />
            </div>

            {/* User Filter */}
            <select
              value={userFilter}
              onChange={(e) => { setUserFilter(e.target.value); setPage(1); }}
              className="cs-body px-3 py-2 rounded-lg"
              style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
            >
              <option value="all">All Users</option>
              {uniqueUsers.map(u => (
                <option key={u.email} value={u.email}>
                  {u.name || u.email}
                </option>
              ))}
            </select>

            {/* Action Type Filter */}
            <select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
              className="cs-body px-3 py-2 rounded-lg"
              style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
            >
              <option value="all">All Actions</option>
              <option value="view">Views</option>
              <option value="edit">Edits</option>
              <option value="download">Downloads</option>
              <option value="share">Shares</option>
              <option value="generate">Generations</option>
              <option value="comment">Comments</option>
              <option value="delete">Deletions</option>
              <option value="invite">Invites</option>
            </select>

            {/* Date Range Filter */}
            <select
              value={dateRange}
              onChange={(e) => { setDateRange(e.target.value); setPage(1); }}
              className="cs-body px-3 py-2 rounded-lg"
              style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
            >
              <option value="1">Today</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>

            {/* Clear Filters */}
            {(searchQuery || userFilter !== 'all' || actionFilter !== 'all' || dateRange !== '7') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setUserFilter('all');
                  setActionFilter('all');
                  setDateRange('7');
                  setPage(1);
                }}
                className="cs-body flex items-center gap-1 px-3 py-2"
                style={{ color: 'var(--color-text-muted)' }}
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            className="cs-body px-4 py-3 rounded-lg mb-6"
            style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}
          >
            {error}
          </div>
        )}

        {/* Activity Table */}
        <div className="rounded-xl overflow-hidden" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 animate-spin" style={{ color: 'var(--color-brand)' }} />
            </div>
          ) : activities.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64" style={{ color: 'var(--color-text-muted)' }}>
              <Activity className="w-12 h-12 mb-3" style={{ color: 'var(--color-text-ghost)' }} />
              <p className="cs-body">No activities found</p>
              <p className="cs-meta">Try adjusting your filters</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead style={{ background: 'var(--color-bg-elevated)', borderBottom: '1px solid var(--color-border)' }}>
                    <tr>
                      <th className="cs-label px-4 py-3 text-left">Time</th>
                      <th className="cs-label px-4 py-3 text-left">User</th>
                      <th className="cs-label px-4 py-3 text-left">Action</th>
                      <th className="cs-label px-4 py-3 text-left">Details</th>
                      <th className="cs-label px-4 py-3 text-left">Case/Client</th>
                      <th className="cs-label px-4 py-3 text-left">IP</th>
                    </tr>
                  </thead>
                  <tbody style={{ borderTop: '1px solid var(--color-border-faint)' }}>
                    {activities.map((activity) => {
                      const config = actionConfig[activity.action_type] || {
                        icon: <Activity className="w-4 h-4" />,
                        color: 'var(--color-text-muted)',
                        bgColor: 'var(--color-bg-elevated)',
                        label: activity.action_type
                      };

                      return (
                        <tr
                          key={activity.id}
                          className="transition"
                          style={{ borderBottom: '1px solid var(--color-border-faint)' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <td className="px-4 py-3">
                            <div className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{formatTime(activity.timestamp)}</div>
                            <div className="cs-meta">{formatFullDate(activity.timestamp)}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{activity.user_name || 'Unknown'}</div>
                            <div className="cs-meta">{activity.user_email}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className="cs-chip inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                              style={{ background: config.bgColor, color: config.color }}
                            >
                              {config.icon}
                              {config.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="cs-body max-w-xs truncate" title={activity.action_details}>
                              {activity.action_details || '-'}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {activity.client_name ? (
                              <div>
                                <div className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{activity.client_name}</div>
                                <div className="cs-figure" style={{ fontSize: '0.75rem' }}>{activity.report_id?.slice(0, 8)}...</div>
                              </div>
                            ) : activity.report_id === 'system' ? (
                              <span className="cs-meta italic" style={{ color: 'var(--color-text-dim)' }}>System</span>
                            ) : (
                              <span className="cs-figure" style={{ fontSize: '0.75rem' }}>{activity.report_id?.slice(0, 8)}...</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="cs-figure" style={{ fontSize: '0.75rem' }}>{activity.ip_address || '-'}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)' }}>
                <div className="cs-meta">
                  Page {page} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
                    style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
                    style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
